import React, { useState } from 'react';
import { FiClock, FiCheck, FiX, FiAlertCircle, FiChevronDown, FiChevronUp, FiCalendar, FiMessageSquare, FiMapPin, FiVideo, FiPhone, FiMail } from 'react-icons/fi';

/**
 * ActiveRequestsPanel - Displays member's introduction requests
 *
 * Shows request details (type, industries, goals, preferences)
 * along with member-friendly status messages.
 * Allows withdrawal of pending requests.
 */

// Map internal status to member-friendly display
const STATUS_DISPLAY = {
  'SUBMITTED': { label: 'Our team is reviewing your request', icon: FiClock, color: 'blue' },
  'TRIAGE': { label: 'Our team is reviewing your request', icon: FiClock, color: 'blue' },
  'IN_REVIEW': { label: 'Our team is reviewing your request', icon: FiClock, color: 'blue' },
  'APPROVED': { label: "We're identifying potential matches", icon: FiClock, color: 'indigo' },
  'STAGED': { label: "We're identifying potential matches", icon: FiClock, color: 'indigo' },
  'SENT': { label: 'Awaiting response from potential match', icon: FiClock, color: 'purple' },
  'AWAITING': { label: 'Awaiting response from potential match', icon: FiClock, color: 'purple' },
  'CONSENTED': { label: 'Match found! Scheduling in progress', icon: FiCheck, color: 'green' },
  'SCHEDULED': { label: 'Session scheduled', icon: FiCalendar, color: 'green' },
  'CONNECTED': { label: 'Connected!', icon: FiCheck, color: 'green' },
  'CLOSED': { label: 'Completed', icon: FiCheck, color: 'gray' },
  'DECLINED': { label: 'Unable to match at this time', icon: FiX, color: 'red' },
  'EXPIRED': { label: 'Request expired', icon: FiAlertCircle, color: 'amber' },
  'WITHDRAWN': { label: 'Request withdrawn', icon: FiX, color: 'gray' },
};

// Request type labels - matches both backend enums and form values
const REQUEST_TYPE_LABELS = {
  'PEER_NETWORKING': 'Peer Networking',
  'PEER_MID_LEVEL': 'Peer Networking',
  'PEER_SENIOR': 'Senior Peer Networking',
  'MENTORSHIP_UP': 'Seek Mentorship',
  'MENTORSHIP_DOWN': 'Offer Mentorship',
  'MENTORSHIP_HIGH': 'Offer Mentorship',
  'BOARD_PATHWAY': 'Board Pathway',
  'HBCU_MENTORSHIP': 'HBCU Mentorship',
};

// Format preference labels
const FORMAT_LABELS = {
  'video_call': { label: 'Video Call', icon: FiVideo },
  'phone_call': { label: 'Phone Call', icon: FiPhone },
  'email_intro': { label: 'Email Introduction', icon: FiMail },
};

const ActiveRequestsPanel = ({ requests, onWithdraw, onViewSession, isLoading }) => {
  const [withdrawingId, setWithdrawingId] = useState(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(null);

  const handleWithdraw = async (requestId) => {
    setWithdrawingId(requestId);
    try {
      await onWithdraw(requestId);
      setShowWithdrawConfirm(null);
    } finally {
      setWithdrawingId(null);
    }
  };

  // Helper to check if request can be withdrawn
  const canWithdraw = (status) => {
    return ['SUBMITTED', 'TRIAGE', 'IN_REVIEW', 'APPROVED', 'STAGED', 'SENT', 'AWAITING', 'CONSENTED', 'SCHEDULED'].includes(status);
  };

  // Helper to check if session view is available
  const hasSession = (status) => {
    return ['SCHEDULED', 'CONNECTED', 'CLOSED'].includes(status);
  };

  // Extract payload fields (handles both frontend and backend key naming)
  const getPayload = (request) => {
    const p = request.request_payload || {};
    return {
      industries: p.desired_industries || p.industries || [],
      functions: p.desired_expertise || p.functions || [],
      geography: p.geographic_preferences || [],
      goals: p.request_context || p.goals || '',
      format: p.additional_preferences?.format_preference || p.format_preference || null,
    };
  };

  const colorClasses = (color) => ({
    bg: color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
        color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
        color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' :
        color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' :
        color === 'amber' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
        color === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
        'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    text: color === 'green' ? 'text-green-600 dark:text-green-400' :
          color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
          color === 'indigo' ? 'text-indigo-600 dark:text-indigo-400' :
          color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
          color === 'amber' ? 'text-amber-600 dark:text-amber-400' :
          color === 'red' ? 'text-red-600 dark:text-red-400' :
          'text-gray-600 dark:text-gray-400',
    border: color === 'green' ? 'border-green-200 dark:border-green-800' :
            color === 'blue' ? 'border-blue-200 dark:border-blue-800' :
            color === 'indigo' ? 'border-indigo-200 dark:border-indigo-800' :
            color === 'purple' ? 'border-purple-200 dark:border-purple-800' :
            color === 'amber' ? 'border-amber-200 dark:border-amber-800' :
            color === 'red' ? 'border-red-200 dark:border-red-800' :
            'border-gray-200 dark:border-gray-700',
  });

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Your Requests
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Your Requests
        </h3>
        <div className="text-center py-8">
          <FiMessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            You haven't submitted any introduction requests yet.
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Use the capabilities above to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Your Requests ({requests.length})
      </h3>

      <div className="space-y-4">
        {requests.map((request) => {
          const statusInfo = STATUS_DISPLAY[request.status] || STATUS_DISPLAY['SUBMITTED'];
          const StatusIcon = statusInfo.icon;
          const colors = colorClasses(statusInfo.color);
          const payload = getPayload(request);
          const formatInfo = payload.format ? FORMAT_LABELS[payload.format] : null;
          const FormatIcon = formatInfo?.icon || null;

          return (
            <div
              key={request.id}
              className={`border rounded-lg overflow-hidden ${colors.border}`}
            >
              {/* Header: Type + Status */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <StatusIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {REQUEST_TYPE_LABELS[request.request_type] || request.request_type}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Submitted {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`text-sm font-medium ${colors.text}`}>
                    {statusInfo.label}
                  </span>
                </div>

                {/* Request Details - Always visible */}
                <div className="space-y-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {/* Goals / Context */}
                  {payload.goals && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Your Goals
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {payload.goals}
                      </p>
                    </div>
                  )}

                  {/* Industries */}
                  {payload.industries.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Industries
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {payload.industries.map((industry) => (
                          <span
                            key={industry}
                            className="px-2 py-0.5 text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full"
                          >
                            {industry}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Functions / Expertise */}
                  {payload.functions.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Functional Areas
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {payload.functions.map((fn) => (
                          <span
                            key={fn}
                            className="px-2 py-0.5 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full"
                          >
                            {fn}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Geography + Format row */}
                  {(payload.geography.length > 0 || formatInfo) && (
                    <div className="flex flex-wrap gap-4">
                      {payload.geography.length > 0 && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                          <FiMapPin className="w-3.5 h-3.5" />
                          <span>{payload.geography.join(', ')}</span>
                        </div>
                      )}
                      {formatInfo && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                          {FormatIcon && <FormatIcon className="w-3.5 h-3.5" />}
                          <span>{formatInfo.label}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {hasSession(request.status) && onViewSession && (
                    <button
                      onClick={() => onViewSession(request.id)}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      View Session Details
                    </button>
                  )}

                  {canWithdraw(request.status) && (
                    showWithdrawConfirm === request.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Are you sure?
                        </span>
                        <button
                          onClick={() => handleWithdraw(request.id)}
                          disabled={withdrawingId === request.id}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg transition-colors"
                        >
                          {withdrawingId === request.id ? 'Withdrawing...' : 'Yes, Withdraw'}
                        </button>
                        <button
                          onClick={() => setShowWithdrawConfirm(null)}
                          className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowWithdrawConfirm(request.id)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors"
                      >
                        Withdraw Request
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActiveRequestsPanel;
